import styled, { css } from "styled-components";
import { ThemeType } from "shared/styles/theme";
import theme from "styled-theming";
import colors from "../styles/colors";
import { buttonBackgroundColorDark, buttonBorderDark, buttonColorDark } from "./button/Button";

const clickableTdColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.grey_2
})

const clickableTdHoverColor = theme("mode", {
    [ThemeType.light]: colors.purpleDark,
    [ThemeType.dark]: colors.grey_1
})
const StyledTableTdBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const StyledTableTdHoverBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.purpleBackground,
    [ThemeType.dark]: colors.dark,
})

const processListTableHightligtedBackgroundColor = theme("mode", {
    [ThemeType.light]: `${colors.purpleBackground}90`,
    [ThemeType.dark]: colors.dark5
})

const processListTableColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

const StyledTableBorderColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_1,
});

const StyledTableHeaderBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.notGreenGrey,
    [ThemeType.dark]: colors.dark3,
});

const tableContainerBorderColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_1
});

export const ClickableTd = styled.td`
    text-decoration-line: underline;
    color: ${clickableTdColor};
    cursor: pointer;
    :hover {
        color: ${clickableTdHoverColor};
    }
`;

export const TruncatedTableContent = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const SharedTableCss = css`
    display: grid;
    thead, tbody, tr {
        display: contents;
    }

    th,  td {
        text-align: start;
        display: flex;
        align-items: center;
        white-space: nowrap;
        overflow: initial; // To get dropdown to go over table

        &.wrap {
            white-space: normal;
        }
    }

    th {
        padding: 25px 21px;
        font-size: var(--header-4-font-size);
        font-weight: 500;
        font-family: "Gilroy-Medium", "medium-fallback";
    }

    td {
        padding: 10px 21px;
    }
`;

// grid-template-rows: 75px; first header row height
export const StyledTable = styled.table<IStyledTableProps>`
    ${SharedTableCss};
    grid-template-columns: ${props => props.gridTemplateColumns ?? `repeat(${props.columnCount}, 1fr)`};
    grid-template-rows: 75px;
    grid-auto-rows: ${props => props.rowHeight ? `${props.rowHeight}px` : "auto"};
    border: 1px solid ${StyledTableBorderColor};
    border-radius: 25px;
    width: 100%;
    border-collapse: collapse;
    th, td {
        ${props => props.shouldWrapData && css`
            white-space: break-spaces !important;
        `}
    }
    th {
        background-color: ${StyledTableHeaderBackgroundColor};
        :first-child {
            border-top-left-radius: 25px;
        }
        :last-child {
            border-top-right-radius: 25px;
        }
    }
    tr {
        &:hover td {
            background-color: ${StyledTableTdHoverBackgroundColor};
        }
        :last-child td {
            :first-child {
                border-bottom-left-radius: 25px;
            }
            :last-child {
                border-bottom-right-radius: 25px;
            }
        }
    }
    td {
        background-color: ${StyledTableTdBackgroundColor};
        border-top: 1px solid ${StyledTableBorderColor};
    }
`

const SharedProcessListTableCSS = css`
    border: unset;
    tr {
        &[data-highlighted='true'] {
            &:hover td {
                background-color: ${StyledTableTdHoverBackgroundColor};

            }
        }
        &[data-highlighted='true'] td {
            // 90 is alpha channel, i.e. transparency
            background-color: ${processListTableHightligtedBackgroundColor};
        }
    }
    td {
        background-color: ${StyledTableTdBackgroundColor};
        height: 60px;
        a {
            color: ${processListTableColor};
        }
        // Skeleton components wrapper
        span[aria-live='polite'][aria-busy='true']{
            width: 100%;
        } 
    }
    th,  td {
        text-align: start;
        display: flex;
        align-items: center;
        white-space: break-spaces;
        overflow: initial; // To get dropdown to go over table
    }
`

export const actionButtonBorderLeft = theme("mode", {
    [ThemeType.light]: `2px solid ${colors.purpleSecondary}`,
    [ThemeType.dark]: `2px solid ${colors.grey_3}`
})

export const actionButtonBorderLeftHover = theme("mode", {
    [ThemeType.light]: `2px solid ${colors.purplePrimary}`,
    [ThemeType.dark]: `2px solid ${colors.grey_3}`
})

const editAndActionButtonBgColorHover = theme("mode", {
    [ThemeType.light]: colors.purpleSecondary,
    [ThemeType.dark]: colors.dark5,
})

export const StyledProcessListTable = styled(StyledTable)`
    ${SharedProcessListTableCSS};
    td {
        height: initial; // Don't restrict Process list td heights, we want table data to wrap as much as possible
    }
    tr {
        &:hover td .process-edit-action-container-selector {
            .process-edit-button-selector,  .process-actions-button-selector {
                color: ${buttonColorDark} !important;
                background-color: ${buttonBackgroundColorDark};
            }
            &:hover .process-actions-button-selector {
                border-left: ${actionButtonBorderLeftHover};
            }
        }
        td .process-edit-action-container-selector {
            .process-edit-button-selector, .process-actions-button-selector {
                &:hover {
                    background-color: ${editAndActionButtonBgColorHover};
                }
            }
            .process-actions-button-selector {
                border-left: ${actionButtonBorderLeft};
            }
        }
    }
`

export const StyledProcessInstanceListLockedTable = styled(StyledTable)`
    ${SharedProcessListTableCSS};
    width: initial;
    tr {
        &:hover td {
            background-color: ${StyledTableTdBackgroundColor};
        }
        td {
            :nth-last-child(2) {
                border-right: 1px solid lightgrey;
            }
            &[class*="_name-selector"] > a {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;  
                overflow: hidden;
            }
        }
        :last-child td {
            :first-child {
                border-bottom-left-radius: 25px;
            }
            :last-child {
                border-bottom-right-radius: 0;
            }
        }
    }
    th {
        :nth-last-child(2){
            border-right: 1px solid lightgrey;
        }
        :last-child {
            border-top-right-radius: 0;
        }
    }
`

export const StyledProcessInstanceListNonLockedTable = styled(StyledTable)`
    ${SharedProcessListTableCSS};
    width: initial;
    tr {
        &:hover td {
            background-color: ${StyledTableTdBackgroundColor};
        }
        :last-child td {
            :first-child {
                border-bottom-left-radius: 0;
            }
            :last-child {
                border-bottom-right-radius: 25px;
            }
        }
    }
    td {
        text-overflow: ellipsis;
    }
    th {
        :first-child {
            border-top-left-radius: 0;
        }
    }
`

const SharedResponsiveTableContainerCSS = css`
    border: 1px solid ${tableContainerBorderColor};
    border-radius: 25px;
`
export const ResponsiveTableContainer = styled.div`
    ${SharedResponsiveTableContainerCSS};
`

export const ResponsiveProcessInstanceListTableContainer = styled.div`
    ${SharedResponsiveTableContainerCSS}
    display: flex;
`
export interface IStyledTableProps {
    gridTemplateColumns?: string;
    columnCount?: number;
    rowHeight?: number;
    shouldWrapData?: boolean;
}

export const SimpleTable = styled.table<ISimpleTableProps>`
    width: 100%;
    th {
        padding-top: 12px;
        padding-bottom: 12px;
        text-align: left;
    }
`

interface ISimpleTableProps {
    textCenter?: boolean;
}